import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { DocumentsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { DocumentFilters, DocumentOrder, DocumentSubjectType, SortOrder } from '@/types'
import { format } from 'date-fns'
import { useTable } from '@/helpers/useTable'
import { ShowDialog } from './show_dialog'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  filters?: DocumentFilters
  createable?: boolean
}

export const Table = ({ pageSize = 10, filters = {}, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<DocumentsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openShow, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.documents.pageInfo, onIndex, basePath: '/documents' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<DocumentFilters, DocumentOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ dateIssued: SortOrder.Desc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.documents.nodes = dedupe([
              ...previousData.documents.nodes,
              ...fetchMoreResult.documents.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, referenceCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'For' },
          { name: 'Reference', sortField: 'reference', defaultDirection: SortOrder.Asc },
          { name: 'Number', sortField: 'number', defaultDirection: SortOrder.Asc },
          { name: 'Date issued', sortField: 'dateIssued', defaultDirection: SortOrder.Desc },
          { name: 'Date due', sortField: 'dateDue', defaultDirection: SortOrder.Desc },
        ]}
        rows={data.documents.nodes.map(document => (
          {
            id: document.id,
            columns: [
              document.subject?.name,
              document.reference,
              document.number,
              format(document.dateIssued, 'dd/MM/yyyy'),
              document.dateDue ? format(document.dateDue, 'dd/MM/yyyy') : '',
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog
        open={openNew}
        setOpen={setOpenNew}
        defaultValues={{
          subjectId: filters.subjectIdEq || '',
          subjectType: filters.subjectTypeEq || DocumentSubjectType.Event
        }}
      />
    </>
  )
}
